import { CalendarUnit } from '@doss/prisma/types'
import { default as dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'
import _ from 'lodash'
import { RRule, rrulestr } from 'rrule'

dayjs.extend(utc)
dayjs.extend(timezone)

const DTSTART_TZID_PATTERN = /^DTSTART;TZID=[^:]+:/m

function toFloatingWallClockDate(date: Date, tz: string): Date {
  const local = dayjs(date).tz(tz)
  return dayjs
    .utc(
      Date.UTC(
        local.year(),
        local.month(),
        local.date(),
        local.hour(),
        local.minute(),
        local.second(),
        local.millisecond(),
      ),
    )
    .toDate()
}

function fromFloatingWallClockDate(date: Date, tz: string): Date {
  return dayjs.tz(dayjs.utc(date).format('YYYY-MM-DDTHH:mm:ss.SSS'), tz).toDate()
}

// Sub-daily frequencies whose occurrence lattice is DTSTART + k × INTERVAL units, so moving
// DTSTART forward by whole steps yields the same occurrences. Longer frequencies are cheap.
const REANCHOR_UNIT_MS: Partial<Record<number, number>> = {
  [RRule.SECONDLY]: 1_000,
  [RRule.MINUTELY]: 60_000,
  [RRule.HOURLY]: 3_600_000,
}

/**
 * `rrule.after()` walks every occurrence from DTSTART, so a MINUTELY rule anchored a year ago
 * costs ~0.6 s per call and grows daily. Re-anchoring DTSTART one step behind `after` keeps the
 * lattice (BY* filters see the same candidates) while making the walk O(1). COUNT rules keep
 * their original anchor, since the count is taken from it.
 */
export function reanchorRule(rule: RRule, after: Date): RRule {
  const { freq, interval, dtstart, count } = rule.options
  const unitMs = REANCHOR_UNIT_MS[freq]
  if (unitMs === undefined || !_.isNil(count)) {
    return rule
  }
  const stepMs = unitMs * interval
  const stepsBehind = Math.floor((after.getTime() - dtstart.getTime()) / stepMs) - 1
  if (stepsBehind < 1) {
    return rule
  }
  return new RRule({
    ...rule.origOptions,
    dtstart: new Date(dtstart.getTime() + stepsBehind * stepMs),
  })
}

function nextOccurrence(pattern: string, after: Date): Date | null {
  const rule = rrulestr(pattern)
  const tz = rule.origOptions.tzid

  if (_.isNil(tz) || tz === '') {
    return reanchorRule(rule, after).after(after, false)
  }

  // TZID RRULEs are evaluated as local wall-clock so we don't inherit the host-timezone-dependent
  // behavior of `rrule.after()`.
  const floatingAfter = toFloatingWallClockDate(after, tz)
  const floatingPattern = pattern.replace(DTSTART_TZID_PATTERN, 'DTSTART:')
  const floatingNext = reanchorRule(rrulestr(floatingPattern), floatingAfter).after(
    floatingAfter,
    false,
  )

  return floatingNext ? fromFloatingWallClockDate(floatingNext, tz) : null
}

// Next occurrence after `nowMs` for an iCal RRULE, plus delay from now. Throws when the rule
// has no future occurrences (e.g. UNTIL has elapsed).
export function computeNextOccurrence(
  pattern: string,
  nowMs: number,
): { nextDate: Date; delayMs: number } {
  const nextDate = nextOccurrence(pattern, dayjs(nowMs).toDate())
  if (!nextDate) {
    throw new Error(`No next occurrence found for pattern ${pattern}`)
  }
  return { nextDate, delayMs: nextDate.getTime() - nowMs }
}

// This is a duplicate of the `rrule.Frequency` type. We have to do this because TS expects
// different styles of imports for our front-end vs back-end module setups so we can't import
// directly from rrule.
enum Frequency {
  YEARLY = 0,
  MONTHLY = 1,
  WEEKLY = 2,
  DAILY = 3,
  HOURLY = 4,
  MINUTELY = 5,
  SECONDLY = 6,
}

/**
 * Helper function to map CalendarUnit to Frequency format
 *
 * Example:
 * Input: CalendarUnit.DAY
 * Output: RRule.DAILY
 *
 * @param frequency CalendarUnit
 * @returns Frequency
 */
function getRRuleFrequencyFromCalendarUnit(frequency: CalendarUnit): Frequency {
  switch (frequency) {
    case CalendarUnit.MINUTE:
      return Frequency.MINUTELY
    case CalendarUnit.HOUR:
      return Frequency.HOURLY
    case CalendarUnit.DAY:
      return Frequency.DAILY
    case CalendarUnit.WEEK:
      return Frequency.WEEKLY
    case CalendarUnit.MONTH:
      return Frequency.MONTHLY
    case CalendarUnit.QUARTER:
      throw new Error('Quarterly frequency is not supported')
    case CalendarUnit.YEAR:
      return Frequency.YEARLY
  }
}

/**
 * Helper function to map days of week from Doss format to RRule format.
 * Doss tracks 0-6 as Sunday-Saturday, but RRule tracks 0-6 as Monday-Sunday.
 *
 * @param daysOfWeek number[]
 * @returns Weekday[]
 */
function getRRuleWeekdaysFromDaysOfWeek(daysOfWeek: number[]): number[] {
  return daysOfWeek.map((day) => (day + 6) % 7)
}

export type FrequencySchedule = {
  daysOfWeek: number[]
  // Restricts firing to the listed hours-of-day (0-23) in the schedule's timezone. Empty/omitted
  // means "any hour" — startAt's hour-of-day is used as today. When set, RRule's BYHOUR expands or
  // filters occurrences accordingly (e.g. DAILY + [9,17] fires twice a day; MINUTELY + [9..17]
  // fires every interval only between 9am and 5pm).
  hoursOfDay?: readonly number[] | null
  repeatNumber: number
  repeatUnit: CalendarUnit
  startAt: Date
}

/**
 * Helper function to convert FrequencySchedule to rrule string
 *
 * Example:
 * Run at 5am every other Saturday and Sunday starting on 2021-01-01
 *
 * Input: {
 *  daysOfWeek: [0, 6],
 *  repeatNumber: 2,
 *  repeatUnit: CalendarUnit.WEEK,
 *  startAt: '2021-01-01T05:00:00.000Z'
 * }
 *
 * Output without timezone: 'DTSTART:20210101T050000Z RRULE:FREQ=WEEKLY;INTERVAL=2;BYDAY=SU,SA'
 * Output with timezone: 'DTSTART;TZID=America/New_York:20210101T000000 RRULE:FREQ=WEEKLY;INTERVAL=2;BYDAY=SU,SA'
 *
 * @param schedule - The frequency schedule configuration
 * @param timezone - Optional IANA timezone string (e.g., 'America/New_York'). When provided,
 *                   the startAt time will be converted from UTC to the specified timezone
 *                   and the RRule will maintain the same local time across DST transitions.
 * @returns RRule string representation
 */
export function frequencyScheduleToRRule(schedule: FrequencySchedule, timezone?: string): string {
  let dtstart: Date

  let tzid: string | undefined

  if (timezone) {
    // When timezone is provided, convert the UTC startAt to local time in that timezone.
    // existing startAt values are stored as UTC, but represent
    // "what local time it should fire in the organization's timezone"
    //
    // For example: If startAt is "2023-03-10T14:00:00Z" (which was 9 AM EST),
    // we want future runs at "9 AM in America/New_York", not "14:00 UTC"

    // Convert the UTC time to the target timezone to extract local time components
    const localMoment = dayjs(schedule.startAt).tz(timezone)

    // Create a "floating" Date where UTC components represent the local time.
    // The rrule library with `tzid` expects dtstart's UTC components to be the local time.
    // NOTE: dayjs.toDate() returns the same UTC instant which is wrong for this purpose —
    // we need the local time components stored as UTC so that RRule treats them as local time.
    dtstart = new Date(
      Date.UTC(
        localMoment.year(),
        localMoment.month(),
        localMoment.date(),
        localMoment.hour(),
        localMoment.minute(),
        localMoment.second(),
      ),
    )
    tzid = timezone
  } else {
    // No timezone: use the existing UTC-based approach
    dtstart = dayjs(schedule.startAt).toDate()
  }

  const ruleOptions: {
    freq: Frequency
    interval: number
    byweekday?: number[]
    byhour?: number[]
    dtstart: Date
    tzid?: string
  } = {
    freq: getRRuleFrequencyFromCalendarUnit(schedule.repeatUnit),
    interval: schedule.repeatNumber,
    dtstart,
    tzid,
  }

  // Only include byweekday if specific days are selected
  if (schedule.daysOfWeek.length > 0) {
    ruleOptions.byweekday = getRRuleWeekdaysFromDaysOfWeek(schedule.daysOfWeek)
  }

  // Only include byhour if specific hours are selected. BYHOUR expands DAILY/WEEKLY/MONTHLY/YEARLY
  // occurrences to fire at each listed hour, and restricts HOURLY/MINUTELY occurrences to those
  // hours. Values are local-time hours when a timezone is provided.
  if (schedule.hoursOfDay && schedule.hoursOfDay.length > 0) {
    ruleOptions.byhour = [...schedule.hoursOfDay]
  }

  const rule = new RRule(ruleOptions)

  return rule.toString()
}

/**
 * Computes a recent startAt date that is close to "now" while preserving the scheduling
 * anchor for the given repeat unit. This avoids a performance issue where the rrule library
 * iterates linearly from the original DTSTART to "now" with expensive timezone conversions
 * on each step.
 *
 * For MINUTE/HOUR/DAY triggers, only the time-of-day matters, so we use yesterday.
 * For WEEK/MONTH/YEAR triggers, the specific date anchors the schedule (e.g., "every month
 * on the 15th"), so we step back by one repeat interval to preserve the correct anchor.
 *
 * @param startAt - The original startAt date (UTC)
 * @param repeatUnit - The calendar unit for the repeat frequency
 * @param repeatNumber - The interval between repeats
 * @param orgTimezone - The organization's IANA timezone
 * @param now - Optional "now" override for testing; defaults to the current time
 * @returns A recent Date (UTC) that preserves the correct scheduling anchor
 */
export function computeRecentStartAt(
  startAt: Date,
  repeatUnit: CalendarUnit,
  repeatNumber: number,
  orgTimezone: string,
  now?: Date,
): Date {
  const originalLocal = dayjs(startAt).tz(orgTimezone)
  const nowLocal = now ? dayjs(now).tz(orgTimezone) : dayjs().tz(orgTimezone)

  // Build a UTC dayjs from year/month/date + the original trigger's time-of-day.
  // We place local-time components into Date.UTC, then shift by the timezone offset
  // to get the real UTC instant. This avoids the offset carryover bugs that occur
  // when chaining dayjs .month()/.date() setters across DST boundaries.
  const buildLocal = (year: number, month: number, date: number): dayjs.Dayjs => {
    const localAsUtc = Date.UTC(
      year,
      month,
      date,
      originalLocal.hour(),
      originalLocal.minute(),
      originalLocal.second(),
    )
    const offsetMs = dayjs(localAsUtc).tz(orgTimezone).utcOffset() * 60_000
    return dayjs.utc(localAsUtc - offsetMs)
  }

  let recentStartAt: dayjs.Dayjs
  switch (repeatUnit) {
    case CalendarUnit.MINUTE:
    case CalendarUnit.HOUR:
    case CalendarUnit.DAY: {
      // Time-of-day is the only anchor — yesterday with the original time is fine.
      const yesterday = nowLocal.subtract(1, 'day')
      recentStartAt = buildLocal(yesterday.year(), yesterday.month(), yesterday.date())
      break
    }
    case CalendarUnit.WEEK: {
      // Preserve the day-of-week and time-of-day. Find the most recent occurrence
      // of the original day-of-week that is strictly before now.
      const originalDow = originalLocal.day()
      let target = nowLocal.day(originalDow)
      if (!target.isBefore(nowLocal)) {
        target = target.subtract(1, 'week')
      }
      recentStartAt = buildLocal(target.year(), target.month(), target.date())
      break
    }
    case CalendarUnit.MONTH:
    case CalendarUnit.QUARTER: {
      // Preserve the day-of-month and time-of-day. Find the most recent occurrence
      // of the original day-of-month that is strictly before now.
      const originalDate = originalLocal.date()
      let target = nowLocal.date(Math.min(originalDate, nowLocal.daysInMonth()))
      if (!target.isBefore(nowLocal)) {
        target = target.subtract(1, 'month')
      }
      recentStartAt = buildLocal(
        target.year(),
        target.month(),
        Math.min(originalDate, target.daysInMonth()),
      )
      break
    }
    case CalendarUnit.YEAR: {
      // Preserve the month, day-of-month, and time-of-day. Find the most recent
      // occurrence of the original month/day that is strictly before now.
      let candidate = buildLocal(nowLocal.year(), originalLocal.month(), originalLocal.date())
      if (!candidate.isBefore(nowLocal)) {
        candidate = buildLocal(nowLocal.year() - 1, originalLocal.month(), originalLocal.date())
      }
      recentStartAt = candidate
      break
    }
  }

  return recentStartAt.utc().toDate()
}

/**
 * Returns the day of the week number as a string
 */
export function dayOfWeekToString(dayOfWeek: number): string {
  switch (dayOfWeek) {
    case 0:
      return 'sunday'
    case 1:
      return 'monday'
    case 2:
      return 'tuesday'
    case 3:
      return 'wednesday'
    case 4:
      return 'thursday'
    case 5:
      return 'friday'
    case 6:
      return 'saturday'
    default:
      throw new Error('Invalid day of week')
  }
}

/**
 * Returns the frequency schedule's user-friendly description.
 *
 * i.e. Repeat: weekly on monday starting on 07/06/2023
 */
export function frequencyScheduleUserFriendly(args: {
  repeatNumber: number
  repeatUnit: string
  daysOfWeek: readonly number[] | null
  hoursOfDay?: readonly number[] | null
  startAt: Date | string | null
}): string {
  let result = 'Repeat: '

  if (args.repeatNumber === 1) {
    if (args.repeatUnit === CalendarUnit.DAY) {
      result += 'daily'
    } else {
      result += `${args.repeatUnit.toLowerCase()}ly`
    }
  } else {
    result += `Every ${args.repeatNumber} ${args.repeatUnit.toLowerCase()}s`
  }

  if (args.daysOfWeek && args.daysOfWeek.length > 0) {
    result += ' on ' + args.daysOfWeek.map(dayOfWeekToString).join(', ')
  } else {
    result += ' on any day of the week'
  }

  if (args.hoursOfDay && args.hoursOfDay.length > 0) {
    result += ' between ' + hoursOfDayToString(args.hoursOfDay)
  }

  if (args.startAt) {
    const dayFormat =
      dayjs(args.startAt).format('MM/DD/YYYY') + ' at ' + dayjs(args.startAt).format('hh:mm A')
    result += ' starting on ' + dayFormat
  }

  return result
}

/**
 * Renders a 0–23 hour as a 12-hour string (e.g. `9` → `"9am"`, `0` → `"12am"`, `12` → `"12pm"`).
 */
export function hourOfDayToString(hour: number): string {
  const period = hour < 12 ? 'am' : 'pm'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}${period}`
}

/**
 * Renders an hours-of-day list as a human-readable string. Contiguous runs collapse into ranges
 * (e.g. `[9,10,11,17]` → `"9am–11am, 5pm"`). Empty list returns the empty string.
 */
export function hoursOfDayToString(hoursOfDay: readonly number[]): string {
  if (hoursOfDay.length === 0) {
    return ''
  }
  const sorted = [...hoursOfDay].sort((a, b) => a - b)
  const ranges: string[] = []
  let runStart = sorted[0]
  let runEnd = sorted[0]
  const pushRun = (): void => {
    ranges.push(
      runStart === runEnd
        ? hourOfDayToString(runStart)
        : `${hourOfDayToString(runStart)}–${hourOfDayToString(runEnd)}`,
    )
  }
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === runEnd + 1) {
      runEnd = sorted[i]
    } else {
      pushRun()
      runStart = sorted[i]
      runEnd = sorted[i]
    }
  }
  pushRun()
  return ranges.join(', ')
}

/**
 * True iff the supplied hours form a single contiguous run when sorted. Used by the dialog to
 * decide whether to render a window slider or a per-hour multi-select. Empty/single-hour lists
 * count as contiguous.
 */
export function isContiguousHoursOfDay(hoursOfDay: readonly number[]): boolean {
  if (hoursOfDay.length <= 1) {
    return true
  }
  const sorted = [...hoursOfDay].sort((a, b) => a - b)
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i - 1] + 1) {
      return false
    }
  }
  return true
}
